import { isSchemaFlagTrue } from './registrySchemaUtils';

/**
 * Registry bindings utilities (Anagrafiche Fase 3.1).
 *
 * A JSON attribute declares `schema.registryBindings: [{ path, registryId, field, required }]`.
 * Paths follow the grammar in the design spec §3.1: `.`-separated segments, each
 * `^[^.\[\]\s]+(\[\*\])?$`, where `key[*]` means "every element of the array at key".
 * Concrete (per-instance) paths returned by the backend use numeric indices: `righe[3].modello`.
 */

export const MSG_PATH_INVALID = 'Percorso non valido';
export const MSG_PATH_DUPLICATE = 'Percorso già associato';
export const MSG_TARGET_MISSING = 'Seleziona anagrafica e campo';
export const MSG_JSON_INVALID = 'JSON non valido';
export const MSG_SCHEMA_NOT_OBJECT = 'Lo schema deve essere un oggetto JSON';
export const MSG_BINDINGS_IN_TEXT = 'Gestisci i binding nella sezione «Binding anagrafiche»';
export const MSG_NO_KEY = 'senza campo chiave';
export const MSG_NOT_KEY = (key) => `Il campo legato deve essere la chiave dell'anagrafica («${key}»)`;
export const MSG_KEY_SEMANTICS = (key) => `I valori estratti dovranno contenere la chiave («${key}»)`;
export const MSG_REGISTRY_MISSING = 'Anagrafica inesistente o non ha voci locali';
export const MSG_REGISTRY_KEYLESS = "L'anagrafica non dichiara un campo chiave";

const SEGMENT_RE = /^[^.[\]\s]+(\[\*\])?$/;
const CONCRETE_SEGMENT_RE = /^([^.[\]\s]+)(?:\[(\d+)\])?$/;
const WILDCARD = '[*]';

/** Nome della proprietà `key: true` dello schema dell'anagrafica, o null. */
export const registryKeyField = (registry) => {
  const props = registry?.schema?.properties || {};
  const found = Object.entries(props).find(([, p]) => p && isSchemaFlagTrue(p.key));
  return found ? found[0] : null;
};

/**
 * Status of a binding/lookup target among the loaded registries with local entries
 * (`registries` null = not loaded yet): { state: 'loading'|'missing'|'keyless'|'ok', key, registry }.
 */
export const registryStatus = (registries, registryId) => {
  if (!Array.isArray(registries)) return { state: 'loading', key: null, registry: null };
  const registry = registries.find((r) => r.id === registryId) || null;
  if (!registry) return { state: 'missing', key: null, registry: null };
  const key = registryKeyField(registry);
  return { state: key ? 'ok' : 'keyless', key, registry };
};

/** Backend rule violated by a target `field` on a registry with this status, or null (also while loading). */
export const registryTargetError = (status, field) => {
  switch (status?.state) {
    case 'missing': return MSG_REGISTRY_MISSING;
    case 'keyless': return MSG_REGISTRY_KEYLESS;
    case 'ok': return field !== status.key ? MSG_NOT_KEY(status.key) : null;
    default: return null;
  }
};

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

export const validateBindingPath = (path) => {
  if (typeof path !== 'string') return MSG_PATH_INVALID;
  const trimmed = path.trim();
  if (!trimmed) return MSG_PATH_INVALID;
  return trimmed.split('.').every((segment) => SEGMENT_RE.test(segment)) ? null : MSG_PATH_INVALID;
};

/**
 * Row errors for a bindings list. Optional `statusOf(registryId)` returns that registry's
 * registryStatus: a missing/keyless registry or a non-key field becomes the row's target error.
 * Without it only the presence of registry and field is checked.
 */
export const validateBindings = (bindings = [], statusOf = null) => {
  const seen = new Set();
  const rows = bindings.map((binding) => {
    const grammarError = validateBindingPath(binding?.path);
    let path = grammarError;
    if (!grammarError) {
      const key = binding.path.trim();
      if (seen.has(key)) path = MSG_PATH_DUPLICATE;
      seen.add(key);
    }
    let target = binding?.registryId && binding?.field ? null : MSG_TARGET_MISSING;
    if (!target && statusOf) target = registryTargetError(statusOf(binding.registryId), binding.field);
    return { path, target };
  });
  return { rows, valid: rows.every((row) => !row.path && !row.target) };
};

export const checkSchemaText = (schemaText) => {
  if (!schemaText || !schemaText.trim()) return null;
  let parsed;
  try {
    parsed = JSON.parse(schemaText);
  } catch (e) {
    return MSG_JSON_INVALID;
  }
  if (!isPlainObject(parsed)) return MSG_SCHEMA_NOT_OBJECT;
  if (Object.prototype.hasOwnProperty.call(parsed, 'registryBindings')) return MSG_BINDINGS_IN_TEXT;
  return null;
};

const normalizeBinding = (binding) => ({
  path: typeof binding?.path === 'string' ? binding.path : '',
  registryId: binding?.registryId || '',
  field: binding?.field || '',
  required: binding?.required === true,
});

const cleanBindings = (bindings = []) =>
  bindings.map((binding) => {
    const normalized = normalizeBinding(binding);
    return { ...normalized, path: normalized.path.trim() };
  });

export const splitSchema = (schema) => {
  if (!isPlainObject(schema)) return { schemaText: '', bindings: [] };
  const { registryBindings, ...rest } = schema;
  const bindings = Array.isArray(registryBindings) ? registryBindings.map(normalizeBinding) : [];
  const schemaText = Object.keys(rest).length > 0 ? JSON.stringify(rest, null, 2) : '';
  return { schemaText, bindings };
};

export const withBindings = (schema, bindings = []) => {
  const { registryBindings, ...rest } = isPlainObject(schema) ? schema : {};
  const clean = cleanBindings(bindings);
  if (clean.length > 0) return { ...rest, registryBindings: clean };
  return Object.keys(rest).length > 0 ? rest : null;
};

export const buildSchema = (schemaText, bindings = []) => {
  const hasText = Boolean(schemaText && schemaText.trim());
  if (!hasText && bindings.length === 0) return null;
  const base = hasText ? JSON.parse(schemaText) : {};
  return withBindings(base, bindings);
};

/** `path → <registry name> · field`; `nameOf(registryId)` falls back to the id when it returns nothing. */
export const formatBindingTarget = (binding, nameOf = null) => {
  const registryId = binding?.registryId || '';
  const registry = (nameOf && nameOf(registryId)) || registryId;
  return `${(binding?.path || '').trim()} → ${registry} · ${binding?.field || ''}`;
};

export const parseTablePath = (path) => {
  if (validateBindingPath(path)) return null;
  const segments = path.trim().split('.');
  const wildcards = segments.reduce((acc, segment, i) => (segment.endsWith(WILDCARD) ? [...acc, i] : acc), []);
  if (wildcards.length !== 1 || wildcards[0] !== segments.length - 2) return null;
  const rootSegments = segments.slice(0, -1);
  const last = rootSegments.length - 1;
  rootSegments[last] = rootSegments[last].slice(0, -WILDCARD.length);
  return { root: rootSegments.join('.'), leaf: segments[segments.length - 1] };
};

export const tableBindings = (bindings = []) => {
  const tables = [];
  const others = [];
  bindings.forEach((binding) => {
    const parsed = parseTablePath(binding?.path);
    if (!parsed) {
      others.push(binding);
      return;
    }
    let table = tables.find((t) => t.root === parsed.root);
    if (!table) {
      table = { root: parsed.root, columns: [] };
      tables.push(table);
    }
    if (!table.columns.some((c) => c.leaf === parsed.leaf)) {
      table.columns.push({ leaf: parsed.leaf, binding });
    }
  });
  return { tables, others };
};

export const indexInstances = (instances) => {
  const index = new Map();
  (Array.isArray(instances) ? instances : []).forEach((instance) => {
    if (instance && typeof instance.path === 'string') index.set(instance.path, instance);
  });
  return index;
};

export const getAtPath = (obj, dottedPath) =>
  dottedPath.split('.').reduce((acc, key) => (isPlainObject(acc) ? acc[key] : undefined), obj);

export const setAtPath = (obj, dottedPath, value) => {
  const [head, ...rest] = dottedPath.split('.');
  const base = isPlainObject(obj) ? obj : {};
  return { ...base, [head]: rest.length > 0 ? setAtPath(base[head], rest.join('.'), value) : value };
};

export const omitAtPath = (obj, dottedPath) => {
  if (!isPlainObject(obj)) return obj;
  const [head, ...rest] = dottedPath.split('.');
  if (!Object.prototype.hasOwnProperty.call(obj, head)) return obj;
  if (rest.length === 0) {
    const { [head]: omitted, ...others } = obj;
    return others;
  }
  return { ...obj, [head]: omitAtPath(obj[head], rest.join('.')) };
};

export const getAtConcretePath = (obj, concretePath) => {
  if (typeof concretePath !== 'string') return undefined;
  return concretePath.split('.').reduce((acc, segment) => {
    const match = CONCRETE_SEGMENT_RE.exec(segment);
    if (!match || !isPlainObject(acc)) return undefined;
    const next = acc[match[1]];
    if (match[2] === undefined) return next;
    return Array.isArray(next) ? next[Number(match[2])] : undefined;
  }, obj);
};

// The backend stores instance values as `String.valueOf(v).trim()`, so compare
// string forms: a numeric 45 in the row must not read as "changed" vs "45".
export const instanceValueChanged = (current, instance) =>
  String(current ?? '').trim() !== String(instance?.value ?? '');
